import {Box, CircularProgress, Paper, Tab, Tabs} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PageSubtitle from '../shared/PageSubtitle';
import TeamPlayerMetricsGraph from './TeamPlayerMetricsGraph';
import {getTeamPlayerMetricsReport} from '../../api/reportAPI';
import {PositionGroup, Positions} from '../../enums';
import {DateTime} from 'luxon';
import {
  Player,
  PlayerMetricsReportPlayer,
  ReportMetricSummary,
} from '../../interfaces';
import {createTrend, getMean} from '../../services/mathService';
import {getSpeedMultiplier} from '../../services/measurementService';
import TeamReportsFilter from './TeamReportsFilter';
import {getXAxisTickPeriod} from '../../services/graphService';

const getMetricMean = ({
  statDateObj,
  date,
  metricKey,
}: {
  statDateObj: Record<string, ReportMetricSummary[]>;
  date: string;
  metricKey: keyof ReportMetricSummary;
}) => {
  return (
    getMean(
      statDateObj[date]
        .filter(d => d[metricKey])
        .map(d => d[metricKey] as number),
    ) ?? undefined
  );
};

const formatDayData = ({
  metricData,
  preferredUnits,
}: {
  metricData: ReportMetricSummary;
  preferredUnits?: string;
}) => ({
  ...metricData,
  activeTime: metricData.activeTime ? metricData.activeTime / 60 : undefined,
  strintTotalTime: metricData.strintTotalTime
    ? metricData.strintTotalTime / 60
    : undefined,
  speedAvgV2: metricData.speedAvgV2
    ? metricData.speedAvgV2 * getSpeedMultiplier(preferredUnits === 'metric')
    : undefined,
  speedMaxV2: metricData.speedMaxV2
    ? metricData.speedMaxV2 * getSpeedMultiplier(preferredUnits === 'metric')
    : undefined,
  accelAvgV2: metricData.accelAvgV2 ? metricData.accelAvgV2 / 9.8 : undefined,
  accelMaxV2: metricData.accelMaxV2 ? metricData.accelMaxV2 / 9.8 : undefined,
  energyRatio: metricData.energyRatio
    ? metricData.energyRatio * 100
    : undefined,
  sessionIntensity: metricData.sessionIntensity
    ? metricData.sessionIntensity * 100
    : undefined,
  balanceNormalized: metricData.balanceNormalized
    ? (metricData.balanceNormalized / 0.5) * 100
    : undefined,
  cumulative3DPlayerLoadV2: metricData.cumulative3DPlayerLoadV2
    ? metricData.cumulative3DPlayerLoadV2 / 100
    : undefined,
});

const getStatMeans = ({
  statDateObj,
  date,
  preferredUnits,
}: {
  statDateObj: Record<string, ReportMetricSummary[]>;
  date: string;
  preferredUnits?: string;
}) => {
  return formatDayData({
    metricData: {
      activeTime: getMetricMean({
        statDateObj,
        date,
        metricKey: 'activeTime',
      }),
      strintTotalTime: getMetricMean({
        statDateObj,
        date,
        metricKey: 'strintTotalTime',
      }),
      strideCountLeft: getMetricMean({
        statDateObj,
        date,
        metricKey: 'strideCountLeft',
      }),
      strideCountRight: getMetricMean({
        statDateObj,
        date,
        metricKey: 'strideCountRight',
      }),
      speedAvgV2: getMetricMean({
        statDateObj,
        date,
        metricKey: 'speedAvgV2',
      }),
      speedMaxV2: getMetricMean({
        statDateObj,
        date,
        metricKey: 'speedMaxV2',
      }),
      accelAvgV2: getMetricMean({
        statDateObj,
        date,
        metricKey: 'accelAvgV2',
      }),
      accelMaxV2: getMetricMean({
        statDateObj,
        date,
        metricKey: 'accelMaxV2',
      }),
      strintFilteredStrideRateAvgV2: getMetricMean({
        statDateObj,
        date,
        metricKey: 'strintFilteredStrideRateAvgV2',
      }),
      strintFilteredStrideRateMaxV2: getMetricMean({
        statDateObj,
        date,
        metricKey: 'strintFilteredStrideRateMaxV2',
      }),
      balance: getMetricMean({
        statDateObj,
        date,
        metricKey: 'balance',
      }),
      balanceNormalized: getMetricMean({
        statDateObj,
        date,
        metricKey: 'balanceNormalized',
      }),
      hrMax: getMetricMean({
        statDateObj,
        date,
        metricKey: 'hrMax',
      }),
      hrAvg: getMetricMean({
        statDateObj,
        date,
        metricKey: 'hrAvg',
      }),
      energyRatio: getMetricMean({
        statDateObj,
        date,
        metricKey: 'energyRatio',
      }),
      sessionIntensity: getMetricMean({
        statDateObj,
        date,
        metricKey: 'sessionIntensity',
      }),
      cumulative3DPlayerLoadV2: getMetricMean({
        statDateObj,
        date,
        metricKey: 'cumulative3DPlayerLoadV2',
      }),
    },
    preferredUnits,
  });
};

const getPositionGroupDataObj = ({
  data,
  positions,
}: {
  data: PlayerMetricsReportPlayer[];
  positions: Positions[];
}) => {
  return (
    data
      .filter(player => positions.indexOf(player.position as Positions) !== -1)
      .flatMap(player => player.data)
      // group by date and get average values for each metric
      .reduce(
        (acc, curr) => {
          if (!acc[curr.date]) {
            acc[curr.date] = [];
          }
          const {date, ...newData} = curr;
          acc[date].push(newData);
          return acc;
        },
        {} as Record<string, ReportMetricSummary[]>,
      )
  );
};

const getPositionGroupDataArray = ({
  dataObj,
  preferredUnits,
}: {
  dataObj: Record<string, ReportMetricSummary[]>;
  preferredUnits?: string;
}) => {
  return Object.keys(dataObj)
    .sort()
    .map(date => ({
      date,
      ...getStatMeans({
        statDateObj: dataObj,
        date,
        preferredUnits,
      }),
    }));
};

const getMetricData = ({
  metricKey,
  allPlayerData,
  format,
  preferredUnits,
}: {
  metricKey: keyof ReportMetricSummary;
  allPlayerData: PlayerMetricsReportPlayer[];
  format: 'positionGroup' | 'player';
  preferredUnits?: string;
}) => {
  if (allPlayerData.length === 0) {
    return null;
  }

  if (format === 'player') {
    return allPlayerData.map(player => {
      const formattedPlayerData = [...player.data]
        .filter(d => d[metricKey])
        .map(d => {
          const formattedData = formatDayData({
            metricData: d,
            preferredUnits,
          });
          return {
            ...formattedData,
            date: d.date,
          };
        });

      return {
        firstName: player.firstName,
        lastName: player.lastName,
        data: formattedPlayerData.map(d => ({
          date: d.date,
          value: d[metricKey] as number,
        })),
        trend: createTrend(
          formattedPlayerData
            .map((d, index) => ({
              x: index,
              y: d[metricKey] as number,
            }))
            .filter(d => d.x && d.y),
          'x',
          'y',
        ),
      };
    });
  } else if (format === 'positionGroup') {
    const allDataObj = getPositionGroupDataObj({
      data: allPlayerData,
      positions: [
        Positions.defense,
        Positions.center,
        Positions.leftwing,
        Positions.rightwing,
      ],
    });
    const allData = getPositionGroupDataArray({
      dataObj: allDataObj,
      preferredUnits,
    });
    const defenseDataObj = getPositionGroupDataObj({
      data: allPlayerData,
      positions: [Positions.defense],
    });
    const defenseData = getPositionGroupDataArray({
      dataObj: defenseDataObj,
      preferredUnits,
    });
    const forwardDataObj = getPositionGroupDataObj({
      data: allPlayerData,
      positions: [Positions.center, Positions.leftwing, Positions.rightwing],
    });
    const forwardData = getPositionGroupDataArray({
      dataObj: forwardDataObj,
      preferredUnits,
    });

    return [
      {
        position: PositionGroup.all,
        data: allData
          .filter(d => d[metricKey])
          .map(d => ({
            date: d.date,
            value: d[metricKey] as number,
          })),
        trend: createTrend(
          allData
            .map((d, index) => ({
              x: index,
              y: d[metricKey] as number,
            }))
            .filter(d => d.x && d.y),
          'x',
          'y',
        ),
      },
      {
        position: PositionGroup.forwards,
        data: forwardData
          .filter(d => d[metricKey])
          .map(d => ({
            date: d.date,
            value: d[metricKey] as number,
          })),
        trend: createTrend(
          forwardData
            .map((d, index) => ({
              x: index,
              y: d[metricKey] as number,
            }))
            .filter(d => d.x && d.y),
          'x',
          'y',
        ),
      },
      {
        position: PositionGroup.defense,
        data: defenseData
          .filter(d => d[metricKey])
          .map(d => ({
            date: d.date,
            value: d[metricKey] as number,
          })),
        trend: createTrend(
          defenseData
            .map((d, index) => ({
              x: index,
              y: d[metricKey] as number,
            }))
            .filter(d => d.x && d.y),
          'x',
          'y',
        ),
      },
    ];
  }
};

interface Props {
  teamName: string;
  teamId: number;
  preferredUnits?: string;
  players: Player[];
}

const TeamMetrics: React.FC<Props> = ({
  teamName,
  teamId,
  preferredUnits,
  players,
}) => {
  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setTabIndex(newValue);
    },
    [],
  );

  const [minDate, setMinDate] = useState<string | undefined>(undefined);
  const [maxDate, setMaxDate] = useState<string | undefined>(undefined);
  const [allPlayerData, setAllPlayerData] = useState<
    PlayerMetricsReportPlayer[]
  >([]);
  const [format, setFormat] = useState<'positionGroup' | 'player'>(
    'positionGroup',
  );

  const fetchData = useCallback(
    async (
      startDate: Date,
      endDate: Date,
      profileIds?: number[],
      sessionTypes?: string[],
    ) => {
      setLoading(true);
      const res = await getTeamPlayerMetricsReport({
        teamId,
        startDate,
        endDate,
        profileIds,
        sessionTypes,
      });
      setMinDate(res.startDate);
      setMaxDate(res.endDate);
      setAllPlayerData(res.data);
      setFormat((profileIds ?? [])?.length > 0 ? 'player' : 'positionGroup');
      setLoading(false);
    },
    [teamId],
  );

  const defaultStartDate = useMemo(() => {
    return DateTime.fromJSDate(new Date()).minus({days: 90}).toJSDate();
  }, []);
  const defaultEndDate = useMemo(() => {
    return new Date();
  }, []);

  // fetch data from api on page load
  useEffect(() => {
    fetchData(defaultStartDate, defaultEndDate);
  }, [teamId, preferredUnits, fetchData, defaultStartDate, defaultEndDate]);

  const activeTimeData = useMemo(
    () =>
      getMetricData({
        metricKey: 'activeTime',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const strintTotalTimeData = useMemo(
    () =>
      getMetricData({
        metricKey: 'strintTotalTime',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const energyRatioData = useMemo(
    () =>
      getMetricData({
        metricKey: 'energyRatio',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const sessionIntensityData = useMemo(
    () =>
      getMetricData({
        metricKey: 'sessionIntensity',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const speedAvgData = useMemo(
    () =>
      getMetricData({
        metricKey: 'speedAvgV2',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const speedMaxData = useMemo(
    () =>
      getMetricData({
        metricKey: 'speedMaxV2',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const accelAvgData = useMemo(
    () =>
      getMetricData({
        metricKey: 'accelAvgV2',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const accelMaxData = useMemo(
    () =>
      getMetricData({
        metricKey: 'accelMaxV2',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const strintFilteredStrideRateAvg = useMemo(
    () =>
      getMetricData({
        metricKey: 'strintFilteredStrideRateAvgV2',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const strintFilteredStrideRateMax = useMemo(
    () =>
      getMetricData({
        metricKey: 'strintFilteredStrideRateMaxV2',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const balanceNormalizedData = useMemo(
    () =>
      getMetricData({
        metricKey: 'balanceNormalized',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const hrAvgData = useMemo(
    () =>
      getMetricData({
        metricKey: 'hrAvg',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const hrMaxData = useMemo(
    () =>
      getMetricData({
        metricKey: 'hrMax',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );
  const cumulative3DPlayerLoadV2Data = useMemo(
    () =>
      getMetricData({
        metricKey: 'cumulative3DPlayerLoadV2',
        allPlayerData,
        format,
        preferredUnits,
      }),
    [allPlayerData, format, preferredUnits],
  );

  const onSubmit = useCallback(
    async ({
      startDate,
      endDate,
      profileIds,
      sessionTypes,
    }: {
      startDate: Date;
      endDate: Date;
      profileIds: number[];
      sessionTypes: string[];
    }) => {
      fetchData(startDate, endDate, profileIds, sessionTypes);
    },
    [fetchData],
  );

  const onReset = useCallback(() => {
    console.log('onReset');
  }, []);

  const xAxisTickPeriod = useMemo(() => {
    // get the number of days between minDate and maxDate
    if (minDate && maxDate) {
      const days = DateTime.fromFormat(maxDate, 'yyyy-MM-dd').diff(
        DateTime.fromFormat(minDate, 'yyyy-MM-dd'),
        'days',
      ).days;
      return getXAxisTickPeriod(days);
    }
    return 30;
  }, [minDate, maxDate]);

  return (
    <Paper sx={{p: {xs: 2, md: 5}}}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}>
        <PageSubtitle sx={{mt: 0}}>
          {t('statistics.playerMetrics')}
        </PageSubtitle>
      </Box>
      <Box>
        <TeamReportsFilter
          players={players}
          onReset={onReset}
          onSubmit={onSubmit}
          defaultStartDate={defaultStartDate}
          defaultEndDate={defaultEndDate}
        />
      </Box>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile={true}>
            <Tab label={t('statistics.strideSpeedTitle')} />
            <Tab label={t('statistics.explosivenessTitle')} />
            <Tab label={t('statistics.agilityTitle')} />
            <Tab label={t('statistics.activityTitle')} />
            <Tab label={t('statistics.balanceTitle')} />
            <Tab label={t('statistics.heartRateTitle')} />
            <Tab label={t('statistics.playerLoadTitle')} />
            <Tab label={t('statistics.practiceIntensityTitle')} />
          </Tabs>
        </Box>
      )}
      <Box hidden={tabIndex !== 0}>
        {speedAvgData && !loading && (
          <TeamPlayerMetricsGraph
            data={speedAvgData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.speedAvgTitle')}
            teamName={teamName}
            metricUnit={
              preferredUnits === 'metric'
                ? (t('general.units.kphAbbrev') as string)
                : (t('general.units.mphAbbrev') as string)
            }
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
        {speedMaxData && !loading && (
          <TeamPlayerMetricsGraph
            data={speedMaxData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.speedMaxTitle')}
            teamName={teamName}
            metricUnit={
              preferredUnits === 'metric'
                ? (t('general.units.kphAbbrev') as string)
                : (t('general.units.mphAbbrev') as string)
            }
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
      <Box hidden={tabIndex !== 1}>
        {accelAvgData && !loading && (
          <TeamPlayerMetricsGraph
            data={accelAvgData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.explosivenessAvgTitle')}
            teamName={teamName}
            metricUnit={t('general.units.gAbbrev') as string}
            metricDecimalPlaces={2}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
        {accelMaxData && !loading && (
          <TeamPlayerMetricsGraph
            data={accelMaxData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.explosivenessMaxTitle')}
            teamName={teamName}
            metricUnit={t('general.units.gAbbrev') as string}
            metricDecimalPlaces={2}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
      <Box hidden={tabIndex !== 2}>
        {strintFilteredStrideRateAvg && !loading && (
          <TeamPlayerMetricsGraph
            data={strintFilteredStrideRateAvg}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.agilityAvgTitle')}
            teamName={teamName}
            metricUnit={t('general.units.spmAbbrev') as string}
            metricDecimalPlaces={0}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
        {strintFilteredStrideRateMax && !loading && (
          <TeamPlayerMetricsGraph
            data={strintFilteredStrideRateMax}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.agilityMaxTitle')}
            teamName={teamName}
            metricUnit={t('general.units.spmAbbrev') as string}
            metricDecimalPlaces={0}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
      <Box hidden={tabIndex !== 3}>
        {activeTimeData && !loading && (
          <TeamPlayerMetricsGraph
            data={activeTimeData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.activeTimeTitle')}
            teamName={teamName}
            metricUnit={t('general.units.minuteAbbrev') as string}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
        {strintTotalTimeData && !loading && (
          <TeamPlayerMetricsGraph
            data={strintTotalTimeData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.strideTimeTitle')}
            teamName={teamName}
            metricUnit={t('general.units.minuteAbbrev') as string}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
        {energyRatioData && !loading && (
          <TeamPlayerMetricsGraph
            data={energyRatioData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.energyRatioTitle')}
            teamName={teamName}
            metricUnit={t('general.units.percentSymbol') as string}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
      <Box hidden={tabIndex !== 4}>
        {balanceNormalizedData && !loading && (
          <TeamPlayerMetricsGraph
            data={balanceNormalizedData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.balanceTitle')}
            teamName={teamName}
            metricUnit={t('general.units.percentSymbol') as string}
            metricDecimalPlaces={1}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
      <Box hidden={tabIndex !== 5}>
        {hrAvgData && !loading && (
          <TeamPlayerMetricsGraph
            data={hrAvgData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.heartRateAvgTitle')}
            teamName={teamName}
            metricUnit={t('general.units.bpmAbbrev') as string}
            metricDecimalPlaces={0}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
        {hrMaxData && !loading && (
          <TeamPlayerMetricsGraph
            data={hrMaxData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.heartRateMaxTitle')}
            teamName={teamName}
            metricUnit={t('general.units.bpmAbbrev') as string}
            metricDecimalPlaces={0}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
      <Box hidden={tabIndex !== 6}>
        {cumulative3DPlayerLoadV2Data && !loading && (
          <TeamPlayerMetricsGraph
            data={cumulative3DPlayerLoadV2Data}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.playerLoadTitle')}
            teamName={teamName}
            metricDecimalPlaces={1}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
      <Box hidden={tabIndex !== 7}>
        {sessionIntensityData && !loading && (
          <TeamPlayerMetricsGraph
            data={sessionIntensityData}
            minDate={minDate}
            maxDate={maxDate}
            metricName={t('statistics.practiceIntensityTitle')}
            teamName={teamName}
            metricUnit={t('general.units.percentSymbol') as string}
            metricDecimalPlaces={1}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        )}
      </Box>
    </Paper>
  );
};

export default TeamMetrics;
